import type { ReactNode } from "react";
import { Gift, Check, Minus, Plus } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { fmtPontos, fmtPts, type PublicProgram, type PublicReward } from "@/lib/loyalty";

export interface PremioEscolhido {
  rewardId: string;
  /** Só no cashback: quantos pontos usar. */
  points?: number;
}

// Cartão "Seus pontos" do checkout (programa de pontos do Ultra). Só mostra o que a loja cadastrou; saldo e troca
// só depois de confirmar o número (o botão abre o código no WhatsApp).
export function PontosDoPedido({
  titulo = "Seus pontos",
  programa,
  verificado,
  saldo,
  escolhidos,
  onEscolher,
  problemaDe,
  rendimento,
  descontoDe,
  pontosUsadosDe,
  onConfirmar,
  aviso,
}: {
  titulo?: string;
  programa: PublicProgram;
  verificado: boolean;
  saldo: number;
  escolhidos: PremioEscolhido[];
  onEscolher: (next: PremioEscolhido[]) => void;
  /** Motivo de o prêmio não servir agora (null = serve). */
  problemaDe: (r: PublicReward) => string | null;
  rendimento: number;
  /** Quanto o prêmio escolhido está descontando (R$). */
  descontoDe: (rewardId: string) => number;
  /** Pontos que o cashback realmente gasta (o pedido limita: não gasta além do que desconta). */
  pontosUsadosDe: (rewardId: string) => number;
  onConfirmar: () => void;
  /** Entra no lugar do «Este pedido rende…» (PDV: cliente sem WhatsApp, que não junta pontos). */
  aviso?: ReactNode;
}) {
  const escolhido = (id: string) => escolhidos.find((e) => e.rewardId === id);
  const alternar = (r: PublicReward) => {
    if (escolhido(r.id)) {
      onEscolher(escolhidos.filter((e) => e.rewardId !== r.id));
      return;
    }
    const novo: PremioEscolhido = r.type === "cashback" ? { rewardId: r.id, points: Math.max(r.cost, saldo) } : { rewardId: r.id };
    onEscolher(programa.oneRewardPerOrder ? [novo] : [...escolhidos, novo]);
  };
  const cashback = escolhidos
    .map((e) => ({ e, r: programa.rewards.find((x) => x.id === e.rewardId) }))
    .find((x) => x.r?.type === "cashback");

  return (
    <div className="bg-card p-4 rounded-2xl shadow border border-border/50 space-y-3" data-testid="checkout-pontos">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Gift className="w-4 h-4 text-primary" /> {titulo}
        </span>
        {verificado && (
          <span className="text-xs font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full" data-testid="checkout-pontos-saldo">
            {fmtPts(saldo)}
          </span>
        )}
      </div>

      {!verificado ? (
        <button
          type="button"
          onClick={onConfirmar}
          className="w-full text-left rounded-xl border border-dashed border-primary/40 bg-primary/5 px-3 py-2.5 text-sm font-medium text-primary"
          data-testid="checkout-pontos-confirmar"
        >
          Confirme seu número para ver e usar seus pontos
        </button>
      ) : programa.rewards.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {programa.rewards.map((r) => {
            const sel = !!escolhido(r.id);
            const problema = sel ? null : problemaDe(r);
            return (
              <button
                key={r.id}
                type="button"
                disabled={!!problema}
                title={problema ?? undefined}
                onClick={() => alternar(r)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  sel
                    ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800"
                    : problema
                      ? "opacity-50 border-border cursor-not-allowed"
                      : "border-border hover:bg-muted"
                }`}
                data-testid={`checkout-premio-${r.id}`}
              >
                {sel && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}
                {r.name}
                {r.type !== "cashback" && <span className="font-normal"> · {fmtPts(r.cost)}</span>}
              </button>
            );
          })}
        </div>
      ) : null}

      {verificado && cashback?.r && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-muted/50 px-3 py-2">
          <span className="text-sm">
            Usar <b>{fmtPontos(pontosUsadosDe(cashback.r.id))}</b>
            <span className="text-muted-foreground"> = {formatCurrency(descontoDe(cashback.r.id))}</span>
          </span>
          <div className="flex items-center gap-1">
            {[-10, 10].map((d) => (
              <button
                key={d}
                type="button"
                aria-label={d < 0 ? "Usar menos pontos" : "Usar mais pontos"}
                className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-card"
                onClick={() =>
                  onEscolher(
                    escolhidos.map((e) =>
                      e.rewardId === cashback.r!.id
                        ? { ...e, points: Math.min(saldo, Math.max(cashback.r!.cost || 1, pontosUsadosDe(cashback.r!.id) + d)) }
                        : e,
                    ),
                  )
                }
              >
                {d < 0 ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {aviso ? (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 dark:bg-amber-950/20 dark:border-amber-900/50 dark:text-amber-200 rounded-lg px-3 py-2" data-testid="checkout-pontos-aviso">
          {aviso}
        </p>
      ) : rendimento > 0 && (
        <p className="text-xs text-primary bg-primary/5 rounded-lg px-3 py-2" data-testid="checkout-pontos-rende">
          Este pedido rende <b>+{fmtPontos(rendimento)}</b> quando for entregue.
        </p>
      )}
    </div>
  );
}
